import { MAX_MODIFIER_QTY } from './modifierQuantity'
import type { GuidedStep, GuidedQuestionStep } from './guidedSteps'

/** Mismo tope que el POS y el kiosko: cabe en un ticket de cocina. */
export const MAX_NOTE_LEN = 280

export type GuidedState = {
  index: number
  /** Paso más lejano alcanzado. Gobierna hasta dónde puede saltar `goTo`. */
  maxReached: number
  /** groupId → optionId → cantidad. */
  selections: Record<string, Record<string, number>>
  note: string
  quantity: number
  /** Se está editando una línea del carrito: apaga el auto-avance por completo. */
  isEditing: boolean
}

export type GuidedAction =
  | { type: 'select'; groupId: string; optionId: string }
  | { type: 'changeQty'; groupId: string; optionId: string; delta: number }
  | { type: 'setNote'; value: string }
  | { type: 'setQuantity'; value: number }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'goTo'; index: number }

export function initialGuidedState(over: Partial<GuidedState> = {}): GuidedState {
  return {
    index: 0,
    maxReached: 0,
    selections: {},
    note: '',
    quantity: 1,
    isEditing: false,
    ...over,
  }
}

export function groupUnits(s: GuidedState, groupId: string): number {
  return Object.values(s.selections[groupId] ?? {}).reduce((sum, q) => sum + q, 0)
}

function isQuestion(step: GuidedStep | undefined): step is GuidedQuestionStep {
  return step?.kind === 'question'
}

/** ¿El paso actual permite avanzar? El paso `extras` siempre sí — todo lo suyo
 *  es opcional. Un `question` exige su mínimo por SUMA de unidades.
 *
 *  Ojo: esto SOLO mira el paso ACTUAL. `goTo` puede saltar a una pregunta ya
 *  contestada, desmarcarla, y saltar de nuevo al último paso sin volver a
 *  pasar por ahí — `canAdvance` del paso `extras` sigue diciendo que sí. Para
 *  gatear el commit hace falta la vista GLOBAL: `allQuestionsSatisfied`. */
export function canAdvance(steps: GuidedStep[], s: GuidedState): boolean {
  const step = steps[s.index]
  if (!isQuestion(step)) return true
  return groupUnits(s, step.group.id) >= step.min
}

/** ¿TODAS las preguntas obligatorias del flujo están satisfechas por su
 *  mínimo? A diferencia de `canAdvance` (que solo mira el paso donde el
 *  cliente está parado), esto recorre TODOS los pasos — es lo único que
 *  detecta el caso de una pregunta contestada, desmarcada por un segundo
 *  toque, y saltada por encima con `goTo` (los tres toques del bug real).
 *  `handleCommit` de ambas vistas debe gatear con esto antes de armar la
 *  línea del carrito, no solo con `canAdvance`. */
export function allQuestionsSatisfied(steps: GuidedStep[], s: GuidedState): boolean {
  return steps.every((step) => !isQuestion(step) || groupUnits(s, step.group.id) >= step.min)
}

/** Índice del primer paso `question` que no cumple su mínimo, o -1 si todos
 *  lo cumplen. Para reenviar al cliente exactamente a la pregunta que falta,
 *  en vez de dejarlo varado en el paso `extras` sin decirle por qué el botón
 *  no funciona. */
export function firstUnsatisfiedQuestionIndex(steps: GuidedStep[], s: GuidedState): number {
  return steps.findIndex((step) => isQuestion(step) && groupUnits(s, step.group.id) < step.min)
}


const optionKey = (groupId: string, optionId: string) => `${groupId}:${optionId}`

/**
 * 86 dentro del flujo: las opciones agotadas que el cliente NO puede elegir.
 * Salen de las preguntas, de los agregados y de las elecciones opcionales del
 * paso extras — no de `remove`: quitar un ingrediente que se acabó ("sin
 * cebolla") es justo lo que la cocina sí puede hacer, y el checkout online lo
 * trata igual (salta `is_removal`, lib/online-orders/pricing.ts).
 */
function soldOutKeys(steps: GuidedStep[]): Set<string> {
  const keys = new Set<string>()
  for (const step of steps) {
    const pool =
      step.kind === 'question' ? step.options : [...step.add, ...step.choiceGroups.flatMap((c) => c.options)]
    for (const o of pool) if (o.soldOut) keys.add(optionKey(o.groupId, o.optionId))
  }
  return keys
}

function withoutKeys(s: GuidedState, keys: Set<string>): GuidedState {
  if (keys.size === 0) return s
  let selections: GuidedState['selections'] | null = null
  for (const [groupId, byOption] of Object.entries(s.selections)) {
    for (const optionId of Object.keys(byOption)) {
      if (!keys.has(optionKey(groupId, optionId))) continue
      if (!selections) selections = { ...s.selections }
      const rest = { ...selections[groupId] }
      delete rest[optionId]
      selections[groupId] = rest
    }
  }
  return selections ? { ...s, selections } : s
}

/**
 * El estado SIN las selecciones de opciones agotadas.
 *
 * Una selección puede quedar apuntando a algo agotado sin que el cliente la
 * toque: editar una línea del carrito precarga lo elegido ANTES del 86, y el
 * menú del kiosko se refresca con la hoja abierta. Si se quedara, seguiría
 * cumpliendo la pregunta obligatoria, ocupando el tope del grupo y sumando al
 * total, y la línea saldría al carrito con el ingrediente que ya no hay.
 *
 * Devuelve el MISMO objeto cuando no hay nada que quitar: el hook la usa en un
 * useMemo, y un objeto nuevo en cada render re-renderizaría la hoja entera.
 */
export function dropSoldOutSelections(steps: GuidedStep[], s: GuidedState): GuidedState {
  return withoutKeys(s, soldOutKeys(steps))
}

export function makeGuidedReducer(steps: GuidedStep[]) {
  const soldOut = soldOutKeys(steps)
  return function reduce(raw: GuidedState, a: GuidedAction): GuidedState {
    // Toda acción parte del estado sin agotados (ver dropSoldOutSelections).
    const s = withoutKeys(raw, soldOut)
    // 86: una opción agotada no responde, ni al toque ni al stepper. Las vistas
    // la pintan apagada, pero la puerta vive acá para que ninguna pueda
    // olvidarla — el kiosko la olvidó desde #1672, y Empanada Factory vendió
    // Mariscos y Huevos de codornis agotados en tres órdenes el 2026-09-13.
    if ((a.type === 'select' || a.type === 'changeQty') && soldOut.has(optionKey(a.groupId, a.optionId))) return s
    switch (a.type) {
      case 'select': {
        const step = steps[s.index]
        const cur = { ...(s.selections[a.groupId] ?? {}) }
        const cap = isQuestion(step) ? step.maxUnits : findExtrasGroupCap(steps, a.groupId, a.optionId)
        // Single-select reemplaza; multi togglea respetando el tope del grupo.
        // Un grupo del paso `extras` es single-select exactamente cuando su
        // tope de unidades es 1 (toda opción de extras es optativa, así que
        // `groupMaxUnits` colapsa a `max_selections` — ver ExtraOption.groupMaxUnits).
        // Reemplazar (no rechazar) es a propósito: es el mismo comportamiento
        // que ItemModal.toggleOption ya tiene hoy para un grupo single-select,
        // required u optativo por igual — un radio real, no un candado que se
        // traba en la primera opción tocada.
        const singleSelect = (isQuestion(step) && step.singleSelect) || (!isQuestion(step) && cap === 1)
        if (singleSelect) {
          return { ...s, selections: { ...s.selections, [a.groupId]: cur[a.optionId] ? {} : { [a.optionId]: 1 } } }
        }
        if (cur[a.optionId]) {
          delete cur[a.optionId]
          return { ...s, selections: { ...s.selections, [a.groupId]: cur } }
        }
        if (groupUnits(s, a.groupId) >= cap) return s
        cur[a.optionId] = 1
        return { ...s, selections: { ...s.selections, [a.groupId]: cur } }
      }

      case 'changeQty': {
        const step = steps[s.index]
        const cur = { ...(s.selections[a.groupId] ?? {}) }
        const cap = isQuestion(step) ? step.maxUnits : findExtrasCap(steps, a.groupId, a.optionId)
        const curQty = cur[a.optionId] ?? 0
        const others = groupUnits(s, a.groupId) - curQty
        let next = Math.min(Math.max(0, curQty + a.delta), MAX_MODIFIER_QTY)
        if (others + next > cap) next = Math.max(0, cap - others)
        if (next === 0) delete cur[a.optionId]
        else cur[a.optionId] = next
        return { ...s, selections: { ...s.selections, [a.groupId]: cur } }
      }

      case 'setNote':
        return { ...s, note: a.value.slice(0, MAX_NOTE_LEN) }

      case 'setQuantity':
        return { ...s, quantity: Math.max(1, Math.floor(a.value)) }

      case 'next': {
        if (!canAdvance(steps, s)) return s
        const index = Math.min(s.index + 1, steps.length - 1)
        return { ...s, index, maxReached: Math.max(s.maxReached, index) }
      }

      case 'back':
        return { ...s, index: Math.max(0, s.index - 1) }

      case 'goTo': {
        if (a.index < 0 || a.index > s.maxReached) return s
        return { ...s, index: a.index }
      }

      default:
        return s
    }
  }
}

/** Tope de REPETICIÓN de una opción puntual del paso de extras (2× queso
 *  extra), para `changeQty`. Sin cambios por este fix: sigue devolviendo el
 *  `maxQty` de la opción — el tope por opción, no el del grupo entero. */
function findExtrasCap(steps: GuidedStep[], groupId: string, optionId: string): number {
  for (const step of steps) {
    if (step.kind !== 'extras') continue
    const found = [...step.add, ...step.remove].find(
      (o) => o.groupId === groupId && o.optionId === optionId
    )
    if (found) return found.maxQty
    for (const cg of step.choiceGroups) {
      if (cg.group.id === groupId) return cg.maxUnits
    }
  }
  return MAX_MODIFIER_QTY
}

/** Tope de UNIDADES del grupo ENTERO dueño de esta opción, cuando vive en el
 *  paso de extras — para `select`, que no repite una opción sino que elige
 *  entre varias DISTINTAS del mismo grupo (add/remove mezclan opciones de
 *  varios grupos en un solo pool, así que hace falta buscar la opción para
 *  saber de qué grupo es). A propósito NO reusa `found.maxQty`: para una
 *  opción no repetible eso es siempre 1 (cuántas veces puedo repetir ESTA
 *  opción), no cuántas opciones distintas admite el grupo — confundirlos
 *  colapsaría a 1 cualquier grupo opcional de elegir-N no pagado (p. ej.
 *  "elige 2 salsas gratis", `max_selections: 2`). Por eso `ExtraOption` trae
 *  `groupMaxUnits` aparte. */
function findExtrasGroupCap(steps: GuidedStep[], groupId: string, optionId: string): number {
  for (const step of steps) {
    if (step.kind !== 'extras') continue
    const found = [...step.add, ...step.remove].find(
      (o) => o.groupId === groupId && o.optionId === optionId
    )
    if (found) return found.groupMaxUnits
    for (const cg of step.choiceGroups) {
      if (cg.group.id === groupId) return cg.maxUnits
    }
  }
  return MAX_MODIFIER_QTY
}

/**
 * ¿Corresponde avanzar solo tras esta acción?
 *
 * `next` es el estado YA REDUCIDO: hace falta para saber si el grupo quedó
 * efectivamente con una selección (un segundo toque sobre la misma opción la
 * deselecciona, y ahí NO se avanza).
 *
 * `isEditing` no es adorno: sin él, la única regla sería "la precarga no
 * dispara auto-avance", que se cumple sola porque la precarga entra por el
 * estado inicial y no por una acción — una prueba imposible de fallar. El caso
 * que sí importa es el cliente que edita una línea, toca otra opción para
 * revisarla, y sale despedido de la pantalla que estaba mirando.
 *
 * El retardo (~220 ms) lo aplica la vista; acá solo se decide.
 */
export function shouldAutoAdvance(
  steps: GuidedStep[],
  next: GuidedState,
  a: GuidedAction
): boolean {
  if (a.type !== 'select') return false
  if (next.isEditing) return false
  const step = steps[next.index]
  if (!isQuestion(step) || !step.singleSelect) return false
  if (next.index >= steps.length - 1) return false
  return groupUnits(next, step.group.id) >= step.min
}
